import { z } from "zod";
import { campaignActionSchema } from "./campaign-config.ts";
import { quotedSpan } from "./quote-span.ts";
import type { ClassificationDecision, ReplyLabels } from "./reply-classification.ts";

/**
 * Replies whose writer is not a prospect working toward the campaign's goal
 * (`docs/2026-09-29-outside-goal-replies.md`; AGENTS.md Boundaries). The app writes no draft and asks
 * no fact question for them: the rep sees that something needs handling and handles it.
 *
 * The list is Dawid's and Vovik's, settled at 16:31 CEST on 2026-09-29: the six certain kinds, the
 * wrong person with nobody named, and a partnership offered back. `nothing_to_reply_to` is the host's
 * own kind for a reply with no words to answer, which went to developer review before.
 *
 * Dawid added the unnamed-contact question exception on 2026-10-09 (d3d727fb).
 *
 * Pure and workflow-safe: the drafting pipeline runs it inside a Temporal workflow.
 */
const OutsideGoalKindSchema = z.enum([
  "leaving_or_refund",
  "client_service",
  "job_application",
  "press_or_media",
  "selling_to_seller",
  "unrelated",
  "wrong_person",
  "partnership_offer",
]);
export type OutsideGoalKind = z.infer<typeof OutsideGoalKindSchema>;
export const OutsideGoalVerdictKindSchema = z.enum([
  ...OutsideGoalKindSchema.options,
  "nothing_to_reply_to",
]);
export type OutsideGoalVerdictKind = z.infer<typeof OutsideGoalVerdictKindSchema>;

const text = z.string().trim().min(1);

/** What the classification call says, before the host has checked any of it. */
export const OutsideGoalLabelSchema = z
  .strictObject({
    kind: OutsideGoalKindSchema,
    quote: text.describe(
      "The writer's own words in the selected message that show it, copied exactly. Never words from quoted history or from the seller.",
    ),
    evidence_id: text.describe("The selected inbound message's evidence id."),
  })
  .describe(
    "Null when the writer is a prospect who could move toward this campaign's goal, whatever their stance. Otherwise the kind of writer, with their own words as evidence.",
  );
export type OutsideGoalLabel = z.infer<typeof OutsideGoalLabelSchema>;

/**
 * The host's verdict. `source` says where it came from: a checked model label, the stance
 * `unrelated_correspondence` (which already means kind `unrelated`), or a reply with no words.
 * `quote` is the reply's own characters for the model's quote, or null when there is none to show.
 */
const OutsideGoalVerdictSchema = z.strictObject({
  kind: OutsideGoalVerdictKindSchema,
  source: z.enum(["model", "stance", "empty_reply"]),
  quote: text.nullable(),
  evidence_id: text,
});
export type OutsideGoalVerdict = z.infer<typeof OutsideGoalVerdictSchema>;

/**
 * What a run records. `rejected` is a model label the host refused, kept so the release evaluation
 * can count it: a failed check means the run went on as before (the recommended choice in the design,
 * "When the check fails"). `applied` says whether the verdict ended the run; it stays false where the
 * workspace's switch is off, the contact-question exception applies, or the rep chose "Draft anyway" (`lifted`).
 */
export const OutsideGoalRecordSchema = z.strictObject({
  version: z.literal(1),
  verdict: OutsideGoalVerdictSchema.nullable(),
  rejected: z
    .strictObject({
      kind: OutsideGoalKindSchema,
      reason: z.enum(["evidence_not_trigger", "quote_not_in_reply"]),
    })
    .nullable(),
  applied: z.boolean(),
  lifted: z.boolean(),
});
export type OutsideGoalRecord = z.infer<typeof OutsideGoalRecordSchema>;

/** Dawid, 2026-10-09 (d3d727fb): a human wrong-person reply with nobody named may ask for a contact. */
export function canAskWrongPerson(
  record: OutsideGoalRecord | undefined,
  labels: ReplyLabels,
  decision: ClassificationDecision,
): boolean {
  return (
    record?.verdict?.kind === "wrong_person" &&
    !["referral_named_with_address", "referral_named_no_address"].includes(labels.route) &&
    labels.stance !== "role_change_departure_auto_notice" &&
    labels.asks.every((ask) => ask === "route_to_person" || ask === "route_to_procurement") &&
    (decision.disposition === "answer" || decision.disposition === "review_trigger")
  );
}

export function wrongPersonQuestionDecision(): ClassificationDecision {
  return {
    disposition: "answer",
    turn_treatment: "normal",
    conditional_yes: false,
    blocked_next_steps: campaignActionSchema.options.filter(
      (action) => action !== "request_clarification",
    ),
  };
}

/**
 * The Temporal patches behind both rules of 2026-09-29, the outside-goal stop and a hostile complaint
 * with no question read as a no. The worker answers them per Bison workspace, as it does the
 * classifier split's (`temporal/patch-activation.ts`), and `-off-v1` lets a switch-off reach a
 * conversation that already recorded `-v1`.
 */
export const OUTSIDE_GOAL_PATCH = "outside-goal-v1";
export const OUTSIDE_GOAL_OFF_PATCH = "outside-goal-off-v1";

/** The run action an applied verdict ends with; `run-projection.ts` turns it into the rep's step. */
/**
 * The classifier prompt a switched-on workspace's classification call reads (`agents/`), frozen from
 * arm A3 plus the outside-goal section; every other call reads A3's own `reply-classifier`.
 */
export const OUTSIDE_GOAL_CLASSIFIER_AGENT = "reply-classifier-outside-goal";
export const OUTSIDE_GOAL_ACTION = "handle_outside_goal";

/**
 * The rep's step for an applied verdict is a `manual` step whose id is
 * `outside-goal-<kind>-r<conversation revision>-c<config revision>` (`run-projection.ts` `stepId`).
 * The id is what tells it from any other chore, in the inbox and on the page.
 */
export const OUTSIDE_GOAL_STEP_PREFIX = "outside-goal";

/**
 * The kinds an outside-goal step can carry: every verdict, and two the host sets without a run.
 * `crosses_campaigns` is a reply the import refused because Bison filed it under a different campaign
 * than the rest of its conversation (docs/2026-10-07-failures-nobody-sees.md): never drafted, since
 * the other campaign's offer may not apply. `not_addressed` is a reply filed under one of the
 * seller's mailboxes but not addressed to it: blind-copied, in no recipient field, or with no mailbox
 * recorded by Bison (docs/2026-10-07-reply-attribution.md); never drafted, since nothing shows it was
 * written to the seller. Both are shown to the rep on this same surface, and both are step kinds
 * only, never a verdict, so no run record carries them.
 */
const OutsideGoalStepKindSchema = z.enum([
  ...OutsideGoalVerdictKindSchema.options,
  "crosses_campaigns",
  "not_addressed",
]);
export type OutsideGoalStepKind = z.infer<typeof OutsideGoalStepKindSchema>;
const CROSSES_CAMPAIGNS_KIND = "crosses_campaigns" satisfies OutsideGoalStepKind;
const NOT_ADDRESSED_KIND = "not_addressed" satisfies OutsideGoalStepKind;
/** The kinds the host sets with no run behind them: there is nothing to draft, ever. */
const SHOWN_ONLY_KINDS = [CROSSES_CAMPAIGNS_KIND, NOT_ADDRESSED_KIND] as const;
export type ShownOnlyKind = (typeof SHOWN_ONLY_KINDS)[number];
export function isShownOnlyKind(kind: OutsideGoalStepKind): kind is ShownOnlyKind {
  return SHOWN_ONLY_KINDS.some((candidate) => candidate === kind);
}

/** The kind an outside-goal step carries, or null for any other step. */
export function outsideGoalStepKind(step: {
  id: string;
  input: { kind: string };
}): OutsideGoalStepKind | null {
  if (step.input.kind !== "manual") return null;
  const match = /^outside-goal-([a-z_]+)-r[0-9]+-c[0-9]+$/u.exec(step.id);
  const kind = OutsideGoalStepKindSchema.safeParse(match?.[1]);
  return kind.success ? kind.data : null;
}

/** The kind in the rep's words, for the row's chip and the sheet. */
export const OUTSIDE_GOAL_LABELS: Record<OutsideGoalStepKind, string> = {
  leaving_or_refund: "Refund or cancellation",
  client_service: "Client service request",
  job_application: "Job application",
  press_or_media: "Press or media",
  selling_to_seller: "Selling to you",
  unrelated: "Unrelated mail",
  wrong_person: "Wrong person",
  partnership_offer: "Partnership offer",
  nothing_to_reply_to: "Nothing to reply to",
  crosses_campaigns: "Filed under another campaign",
  not_addressed: "Not addressed to your mailbox",
};

/**
 * A reply with no words to answer: an empty body, a subject line alone, or only an image. Image and
 * attachment placeholders the mail carries as text (`[image: logo.png]`, `[cid:…]`) are not words.
 *
 * `null` is the extractor saying it could not separate the writer's words from the quoted history
 * (`ambiguous`, such as "No" above a Gmail quote). The stages then read the whole body and draft, so
 * an unknown is never "nothing to reply to": the census replay found 16 of its first 135 real leads
 * lost that way before this line.
 */
export function isEmptyReply(authoredText: string | null): boolean {
  if (authoredText === null) return false;
  const words = authoredText
    // The extractor keeps the author's signature for identity. A standard signature delimiter
    // does not turn an image-only reply into words to answer; prose before it still counts.
    .replace(/\n--[ \t]+\n[\s\S]*$/u, "")
    .replace(/\[(?:image|cid)\b[^\]]*\]/giu, "")
    .replace(/<cid:[^>]*>/giu, "");
  return !/\p{L}/u.test(words);
}

/**
 * The host's check of what the classification call said about the writer. A model label stands only
 * when it names the selected message and its quote is found in that message's own authored text,
 * with whitespace tolerated as every other model quote is (`quote-span.ts`). The authored text is the
 * message without its quoted history, so words from an older message never count as the writer's.
 */
export function outsideGoalRecord(input: {
  label: OutsideGoalLabel | null;
  stance: string;
  triggerText: string | null;
  triggerEvidenceId: string;
}): OutsideGoalRecord {
  const base = { version: 1 as const, applied: false, lifted: false };
  const { label, triggerText, triggerEvidenceId } = input;
  if (label) {
    const span =
      label.evidence_id === triggerEvidenceId ? quotedSpan(triggerText ?? "", label.quote) : null;
    if (span !== null)
      return {
        ...base,
        verdict: { kind: label.kind, source: "model", quote: span, evidence_id: triggerEvidenceId },
        rejected: null,
      };
    const rejected = {
      kind: label.kind,
      reason:
        label.evidence_id === triggerEvidenceId
          ? ("quote_not_in_reply" as const)
          : ("evidence_not_trigger" as const),
    };
    return { ...base, verdict: fallbackVerdict(input), rejected };
  }
  return { ...base, verdict: fallbackVerdict(input), rejected: null };
}

function fallbackVerdict(input: {
  stance: string;
  triggerText: string | null;
  triggerEvidenceId: string;
}): OutsideGoalVerdict | null {
  if (isEmptyReply(input.triggerText))
    return {
      kind: "nothing_to_reply_to",
      source: "empty_reply",
      quote: null,
      evidence_id: input.triggerEvidenceId,
    };
  if (input.stance === "unrelated_correspondence")
    return {
      kind: "unrelated",
      source: "stance",
      quote: null,
      evidence_id: input.triggerEvidenceId,
    };
  return null;
}

/** The description the run's action carries: what an older build shows as the step's text. */
export function outsideGoalActionDescription(verdict: OutsideGoalVerdict): string {
  return verdict.kind === "nothing_to_reply_to"
    ? "Nothing to reply to: the message has no words to answer, so nothing was drafted. Handle it yourself if it needs anything."
    : `Not a sales reply (${OUTSIDE_GOAL_LABELS[verdict.kind].toLowerCase()}): the writer is not a prospect for this campaign, so nothing was drafted. Handle it yourself.`;
}
